import { foldCode, foldText, foldWithIndexMap, originalRange } from '@/shared/utils/searchFold';

/**
 * QF-01 / QF-02 — what the client may say about a match, from text it already
 * holds. The server alone decides WHETHER a record matches (SearchSql: the
 * whole folded term as one substring of a searched field); these only show
 * WHERE, and whether a returned code is the very code typed. Nothing here is
 * sent anywhere, and nothing is inferred about a record the server did not
 * return (search map R1).
 */

/** `[start, end)` in the text as written. */
export type TextRange = readonly [number, number];

// A letter, mark, digit or tatweel: what an Arabic word is made of.
const WORD_UNIT = /[\p{L}\p{M}\p{N}ـ]/u;
const ARABIC = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufeff]/u;

/** Sorted, with overlapping or touching ranges joined. */
export function mergeRanges(ranges: readonly TextRange[]): TextRange[] {
  const sorted = ranges.filter(([from, to]) => to > from).sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];

  for (const [from, to] of sorted) {
    const last = merged.at(-1);

    if (last !== undefined && from <= last[1]) last[1] = Math.max(last[1], to);
    else merged.push([from, to]);
  }

  return merged;
}

/**
 * Each range that touches Arabic, widened to the whole word(s) it is in.
 * Splitting an Arabic word across elements can break its cursive joining in
 * some browsers (search map R3), so an Arabic match is marked word by word.
 * Latin text and codes keep their exact range.
 */
export function wholeArabicWords(text: string, ranges: readonly TextRange[]): TextRange[] {
  return mergeRanges(
    ranges.map(([from, to]): TextRange => {
      if (!ARABIC.test(text.slice(from, to))) return [from, to];

      let start = from;
      let end = to;

      while (start > 0 && WORD_UNIT.test(text.charAt(start - 1))) start -= 1;
      while (end < text.length && WORD_UNIT.test(text.charAt(end))) end += 1;

      return [start, end];
    }),
  );
}

/**
 * Where the folded term occurs in the text, as the server matches a record
 * field: the whole term, as one substring, folded the same way (Arabic letter
 * forms, harakat, tatweel, Arabic-Indic digits, case). Every occurrence, in
 * the text as written.
 */
export function substringRanges(text: string | null | undefined, term: string): TextRange[] {
  if (!text) return [];

  const needle = foldText(term);

  if (needle === '') return [];

  const map = foldWithIndexMap(text);
  const ranges: TextRange[] = [];

  for (
    let at = map.folded.indexOf(needle);
    at !== -1;
    at = map.folded.indexOf(needle, at + needle.length)
  ) {
    ranges.push(originalRange(map, at, at + needle.length));
  }

  return wholeArabicWords(text, ranges);
}

/**
 * QF-02 — whether a returned code IS the code typed: compared as a code is
 * (NFKC, controls and spacing gone, Arabic-Indic digits as 0–9, case-blind;
 * letters otherwise as written). Only ever asked of a row the server returned.
 */
export function isExactCode(code: string | null | undefined, term: string): boolean {
  if (!code) return false;

  const typed = foldCode(term);

  return typed !== '' && foldCode(code) === typed;
}
